import { CampaignParticipants } from './components';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    ButtonFooter,
    Card,
    Checkbox,
    CheckboxGroup,
    DateSelect,
    DragAndDrop,
    ExpandButton,
    FormGroup,
    Input,
    TagList,
    Text
} from 'mol-ui';
import {
    CAMPAIGN_TYPES,
    campaignActions,
    parseCampaignParticipantStatus,
    parseCampaignType
} from 'core/Campaign';
import {
    CampaignTypePicker,
    QuizPicker,
    VaccinePicker
} from 'views/pickers';
import { Col, Row } from 'reactstrap';
import {
    ErrorMessage,
    FileList,
    FormLayout
} from 'views/components';
import React, { useCallback, useEffect } from 'react';
import {
    clearObject,
    dateToObject,
    objectToDate,
    validationSchema
} from 'shared/utils';
import {
    useArray,
    useDispatch,
    useErrors,
    useFileUpload,
    useInput,
    useObjectState,
    useSelector,
    useWindowSize
} from 'hooks';
import {
    useLocation,
    useParams
} from 'react-router-dom';

const DEFAULT_CAMPAIGN = {
    address: '',
    effective_date: moment(),
    effective_time: moment().format('HH:mm'),
    files: [],
    invitation_text: '',
    mandatory: false,
    name: '',
    start_date: moment(),
    start_time: moment().format('HH:mm'),
    survey_id: undefined,
    type: undefined,
    type_id: undefined,
    vaccine_id: null
};

const ABSENT = 'absent';
const ACCEPTED = 'accepted';
const INVITED = 'invited';
const PRESENT = 'present';
const REGISTERED = 'registered';
const REJECTED = 'rejected';

const CampaignRegistration = () => {

    const { id: idParam } = useParams();
    const { pathname } = useLocation();
    const id = Number(idParam ?? 0);
    const { t } = useTranslation();
    const createMode = pathname.split('/')[2] !== t('editar');
    const cloneMode = createMode && !!id;
    const dispatch = useDispatch();
    const { xs } = useWindowSize();

    const { clearError, errors, setErrors } = useErrors();
    const [cloneParticipantsStatus,, { replaceValues: replaceParticipantClone }] = useArray(
        [INVITED, REGISTERED, ACCEPTED, REJECTED, PRESENT, ABSENT].map(e => ({
            id: e,
            label: parseCampaignParticipantStatus(e).label,
            value: false
        }
        )), { type: 'object' });
    const [currentFiles, setCurrentFiles, { push: addFile, remove: removeFile }] = useArray([], { type: 'object' });
    const [form, setForm, setFormValue] = useObjectState({ ...DEFAULT_CAMPAIGN });
    const [managerDescriptionInput, managerDescription, setManagerDescription] = useInput('');
    const [managerEmailInput, managerEmail, setManagerEmail] = useInput('');
    const [managers, setManagers, {
        find: findManager,
        push: pushManager,
        removeByIndex: removeManager
    }] = useArray([], { type: 'object' });
    const [uploadFile, loadingFile] = useFileUpload();
    const onlineCampaign = [
        CAMPAIGN_TYPES.communication,
        CAMPAIGN_TYPES.healthSurvey,
        CAMPAIGN_TYPES.quiz
    ].includes(form?.type);

    const {
        loading,
        processing,
        selected
    } = useSelector('campaign');

    useEffect(() => {
        setForm({ ...DEFAULT_CAMPAIGN });
        setCurrentFiles([]);
        setManagers([]);
        if (!id) return;
        dispatch(campaignActions.getCampaign(id));
    }, [id, setForm, setManagers]);

    useEffect(() => {
        if (isEmpty(selected)) return;
        const { effective_datetime, files, managers, start_datetime, vaccine } = selected;
        setForm({
            ...selected,
            effective_date: moment(effective_datetime),
            effective_time: moment(effective_datetime).format('HH:mm'),
            start_date: moment(start_datetime),
            start_time: moment(start_datetime).format('HH:mm'),
            vaccine_id: vaccine?.id ?? null
        });
        setCurrentFiles(files);
        setManagers(managers);
    }, [selected]);

    const handleAddManager = () => {
        if (!managerDescription || !managerEmail) return;
        const newManager = {
            description: managerDescription,
            email: managerEmail
        };
        pushManager(newManager);
        setManagerDescription('');
        setManagerEmail('');
    };

    const handleChange = (id, value) => {
        clearError(id);
        if (id === 'type') {
            handleChange('name', '');
            handleChange('vaccine_id', null);
        }
        setFormValue(id, value);
    };

    const handleFileUpload = async file => {
        const { data } = await uploadFile(file);
        addFile(data);
    };

    const onSubmit = useCallback(() => {
        if (!validate()) return;
        const data = {
            ...form,
            ...(cloneMode && {
                clone_id: id,
                clone_status: cloneParticipantsStatus.filter(({ value }) => !!value).map(({ id }) => id)
            }),
            effective_datetime: !onlineCampaign ? `${ form?.effective_date?.format('YYYY-MM-DD') } ${ form?.effective_time }` : null,
            files: form?.type === CAMPAIGN_TYPES.communication ? currentFiles.map(({ id }) => id) : [],
            id: !createMode ? id : undefined,
            managers,
            start_datetime: `${ form?.start_date?.format('YYYY-MM-DD') } ${ form?.start_time }`
        };
        dispatch(campaignActions.upsertCampaign(clearObject(data)));
    }, [createMode, cloneMode, cloneParticipantsStatus, currentFiles, form, id, onlineCampaign]);

    const validate = useCallback(() => {
        const { errors, isValid } = validationSchema({
            address: [!!form?.address || onlineCampaign, t('Requerido')],
            name: [![CAMPAIGN_TYPES.communication, CAMPAIGN_TYPES.training].includes(form?.type) || !!form?.name, t('Requerido')],
            survey_id: [form?.type !== CAMPAIGN_TYPES.quiz || !!form?.survey_id || !createMode, t('Requerido')],
            vaccine_id: [form?.type !== CAMPAIGN_TYPES.vaccination || !!form?.vaccine_id, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    }, [createMode, form, onlineCampaign]);

    const addManagerDisabled = !managerDescription || !managerEmail || !!findManager('email', managerEmail);
    const title = createMode ? t('Creando campaña') : t('Editando campaña');

    return (
        <FormLayout
            goBackTo={ t('/campanas') }
            loading={ loading }
            title={ title }
        >
            <Accordion defaultOpened={ createMode } margin="0px 0px 20px 0px" title={ t('Datos generales') }>
                <Row>
                    <Col xs="12">
                        <FormGroup label={ t('Tipo') } margin="0px 0px 20px 0px">
                            {
                                createMode &&
                                <CampaignTypePicker
                                    objectChange
                                    filter={ ({ active }) => Boolean(active) }
                                    margin="0px"
                                    value={ form?.type_id }
                                    onChange={ ({ id, key }) => {
                                        handleChange('type', key);
                                        handleChange('type_id', id);
                                    } }
                                />
                            }
                            {
                                !createMode &&
                                <Text>{ parseCampaignType(form?.type) }</Text>
                            }
                        </FormGroup>
                    </Col>
                    {
                        [CAMPAIGN_TYPES.communication, CAMPAIGN_TYPES.training].includes(form?.type) &&
                        <Col xs="12">
                            <FormGroup label={ t('Nombre de la campaña') } margin="0px 0px 20px 0px">
                                <Input
                                    error={ errors?.name }
                                    margin="0px"
                                    value={ form?.name ?? '' }
                                    onChange={ value => handleChange('name', value) }
                                />
                            </FormGroup>
                        </Col>
                    }
                    {
                        form?.type === CAMPAIGN_TYPES.vaccination &&
                        <Col xs="12">
                            <FormGroup label={ t('Vacuna') } margin="0px 0px 20px 0px">
                                {
                                    createMode &&
                                    <>
                                        <VaccinePicker
                                            hasError={ !!errors?.vaccine_id }
                                            value={ form?.vaccine_id }
                                            onChange={ value => handleChange('vaccine_id', value) }
                                        />
                                        <ErrorMessage relative error={ errors?.vaccine_id } margin="0px" />
                                    </>
                                }
                                {
                                    !createMode &&
                                    <Text>{ form?.vaccine?.name }</Text>
                                }
                            </FormGroup>
                        </Col>
                    }
                    {
                        form?.type === CAMPAIGN_TYPES.quiz &&
                        <Col xs="12">
                            <FormGroup label={ t('Encuesta') } margin="0px 0px 20px px">
                                {
                                    createMode &&
                                    <>
                                        <QuizPicker
                                            hasError={ !!errors?.survey_id }
                                            value={ form?.survey_id }
                                            onChange={ value => handleChange('survey_id', value) }
                                        />
                                        <ErrorMessage relative error={ errors?.survey_id } margin="0px" />
                                    </>
                                }
                                {
                                    !createMode &&
                                    <Text margin="0px 0px 10px 0px">{ form?.survey?.name }</Text>
                                }
                            </FormGroup>
                        </Col>
                    }
                    <Col xs="12">
                        <Checkbox
                            checked={ !!form?.mandatory }
                            label={ t('Obligatoria') }
                            margin="0px 0px 20px 0px"
                            onClick={ value => handleChange('mandatory', value) }
                        />
                    </Col>
                    <Col sm="6" xs="12">
                        <FormGroup label={ t('Fecha de inicio') }>
                            <DateSelect
                                margin="0px"
                                nextYears={ 1 }
                                value={ dateToObject(form?.start_date) }
                                onChange={ value => handleChange('start_date', objectToDate(value)) }
                            />
                        </FormGroup>
                    </Col>
                    <Col sm="6" xs="12">
                        <FormGroup label={ t('Hora de inicio') }>
                            <Input
                                type="time"
                                value={ form?.start_time ?? '' }
                                onChange={ value => handleChange('start_time', value) }
                            />
                        </FormGroup>
                    </Col>
                    {
                        !onlineCampaign &&
                        <>
                            <Col xs="12">
                                <FormGroup label={ t('Ubicación') } margin="0px 0px 20px 0px">
                                    <Input
                                        error={ errors?.address }
                                        margin="0px"
                                        value={ form?.address ?? '' }
                                        onChange={ value => handleChange('address', value) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Fecha de realización') }>
                                    <DateSelect
                                        nextYears={ 1 }
                                        value={ dateToObject(form?.effective_date) }
                                        onChange={ value => handleChange('effective_date', objectToDate(value)) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col sm="6" xs="12">
                                <FormGroup label={ t('Hora de realización') }>
                                    <Input
                                        type="time"
                                        value={ form?.effective_time ?? '' }
                                        onChange={ value => handleChange('effective_time', value) }
                                    />
                                </FormGroup>
                            </Col>
                        </>
                    }
                    <Col xs="12">
                        <FormGroup label={ form?.type === CAMPAIGN_TYPES.healthSurvey ? t('Texto para el correo') : t('Texto para la invitación') } margin="0px 0px 20px 0px">
                            <Input
                                height="120px"
                                margin="0px"
                                type="area"
                                value={ form?.invitation_text ?? '' }
                                onChange={ value => handleChange('invitation_text', value) }
                            />
                        </FormGroup>
                    </Col>
                    {
                        form?.type === CAMPAIGN_TYPES.communication &&
                        <>
                            <Col xs="12">
                                <FormGroup label={ t('Contenido') } margin="0px 0px 20px 0px">
                                    <Input
                                        height="120px"
                                        margin="0px"
                                        type="area"
                                        value={ form?.information ?? '' }
                                        onChange={ value => handleChange('information', value) }
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs="12">
                                <FormGroup label={ t('Archivos') } margin="0px 0px 20px 0px">
                                    <FileList
                                        files={ currentFiles }
                                        onDelete={ removeFile }
                                    />
                                    <DragAndDrop
                                        clearFileAfterChange
                                        loading={ loadingFile }
                                        onFileChange={ handleFileUpload }
                                    />
                                </FormGroup>
                            </Col>
                        </>
                    }
                    <Col xs="12">
                        <FormGroup label={ t('Encargados') }>
                            <Row>
                                <Col sm="4" xs="12">
                                    <FormGroup label={ t('Email') } margin="10px 0px 0px 0px">
                                        <Input
                                            { ...managerEmailInput }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm="4" xs="12">
                                    <FormGroup label={ t('Descripción') } margin="10px 0px 0px 0px">
                                        <Input
                                            { ...managerDescriptionInput }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm="4" xs="12">
                                    <ExpandButton
                                        circle
                                        disabled={ addManagerDisabled }
                                        icon="add"
                                        margin={ !xs ? '30px 0px 0px 0px' : '0px' }
                                        size={ 40 }
                                        onClick={ handleAddManager }
                                    />
                                </Col>
                            </Row>
                            <TagList
                                accessor={ ({ description, email }) => `${ description }: ${ email }` }
                                items={ managers }
                                margin={ xs ? '10px 0px 0px 0px' : '0px' }
                                onRemove={ (item, index) => removeManager(index) }
                            />
                        </FormGroup>
                    </Col>
                </Row>
                {
                    cloneMode &&
                    <Card margin="20px 0px 0px 0px">
                        <FormGroup label={ t('Copiar invitados en estado') }>
                            <CheckboxGroup
                                columns={{ lg: 3, md: 3, xs: 2 }}
                                options={ cloneParticipantsStatus }
                                onClick={ ({ id, value }) => replaceParticipantClone({ value: !value }, 'id', id) }
                            />
                        </FormGroup>
                    </Card>
                }
                <ButtonFooter margin="20px 0px">
                    <ExpandButton
                        caption={ t('Guardar') }
                        loading={ processing }
                        size={ 80 }
                        onClick={ onSubmit }
                    />
                </ButtonFooter>
            </Accordion>
            {
                !createMode && !!id &&
                <CampaignParticipants id={ id } />
            }
        </FormLayout>
    );
};

export default CampaignRegistration;
